import React, {useMemo} from 'react';

import {EPopover} from '@ELib/components/EPopover';
import {Rect, RectProps} from '@ELib/utilities/rect';
import {Positioning} from '@ELib/utilities/types';

const calculateDimensions = (
  dimension: HTMLElement | SVGElement | Rect | number | undefined | null,
  name: 'width' | 'height'
): number | undefined => {
  if (dimension === undefined) {
    return undefined;
  }

  if (dimension === null) {
    return 0;
  }

  if (typeof dimension === 'number') {
    return dimension;
  }

  if (dimension instanceof HTMLElement) {
    return dimension.getBoundingClientRect()[name];
  }

  return undefined;
};

type OrientationType = {
  vertical: 'top' | 'center' | 'bottom';
  horizontal: 'left' | 'center' | 'right';
};

type PopoverPositioning = {
  origin?: OrientationType;
  popover?: OrientationType;
};

const usePositioningProps = (pos: Positioning): PopoverPositioning =>
  useMemo(() => {
    switch (pos) {
      case Positioning.BottomLeftAlignDown:
        return {
          origin: {horizontal: 'left', vertical: 'bottom'},
          popover: {horizontal: 'left', vertical: 'top'},
        };
      case Positioning.BottomRightAlignDown:
        return {
          origin: {horizontal: 'right', vertical: 'bottom'},
          popover: {horizontal: 'right', vertical: 'top'},
        };
      case Positioning.TopLeftAlignDown:
        return {
          origin: {horizontal: 'left', vertical: 'top'},
          popover: {horizontal: 'left', vertical: 'top'},
        };
      case Positioning.TopRightAlignDown:
        return {
          origin: {horizontal: 'right', vertical: 'top'},
          popover: {horizontal: 'right', vertical: 'top'},
        };
      case Positioning.TopRightOutsetDown:
        return {
          origin: {horizontal: 'right', vertical: 'top'},
          popover: {horizontal: 'left', vertical: 'top'},
        };
      case Positioning.TopLeftOutsetDown:
        return {
          origin: {horizontal: 'left', vertical: 'top'},
          popover: {horizontal: 'right', vertical: 'top'},
        };
      default:
        throw new Error('Wrong positioning');
    }
  }, [pos]);

type Props = {
  width?: HTMLElement | SVGElement | number | null;
  height?: HTMLElement | SVGElement | number | null;
  left?: number;
  top?: number;
  target?: null | Element | (() => Element);
  content: React.ReactElement;
  disableDrag?: boolean;
  frameElement?: HTMLElement | null;
  horizontalAlternatives?: RectProps[];
  isVisible?: boolean;
  margin?: number;
  mirrorOnOverflow?: boolean;
  testid?: string;
  verticalAlternatives?: RectProps[];
  /**
   * changing the width via the width property destroys the positioning of the popup element when centered
   * to an input element, so this prop forces the auto width without destroying the alignment
   */
  forceAutoWidth?: boolean;
  onClose?: (evt: React.MouseEvent) => void;
  placement: Positioning;
  disableBackdrop?: boolean;
  disablePortal?: boolean;
  disableAutoFocus?: boolean;
  disableEnforceFocus?: boolean;
};

interface ReturnType {
  popup?: React.ReactElement;
}

export const usePopup = ({
  content,
  height: height_,
  isVisible = true,
  target,
  width: width_,
  placement,
  forceAutoWidth = false,
  disableBackdrop = false,
  disablePortal = false,
  disableAutoFocus = false,
  disableEnforceFocus = false,
  top = 0,
  left = 0,
  onClose,
}: Props): ReturnType => {
  const width = calculateDimensions(width_, 'width');
  const height = calculateDimensions(height_, 'height');

  const {origin, popover} = usePositioningProps(placement);
  const popup = useMemo(
    () => (
      <EPopover
        anchorEl={target}
        anchorOrigin={origin}
        anchorPosition={target ? undefined : {top, left}}
        anchorReference={target ? undefined : 'anchorPosition'}
        disableAutoFocus={disableAutoFocus}
        disableEnforceFocus={disableEnforceFocus}
        disablePortal={disablePortal}
        height={height == null ? undefined : `${height}px`}
        hideBackdrop={disableBackdrop}
        open={isVisible}
        transformOrigin={popover}
        width={width == null || forceAutoWidth ? undefined : `${width}px`}
        onClose={onClose}
      >
        {content}
      </EPopover>
    ),
    [
      target,
      origin,
      top,
      left,
      disableAutoFocus,
      disableEnforceFocus,
      disablePortal,
      height,
      disableBackdrop,
      isVisible,
      popover,
      width,
      forceAutoWidth,
      onClose,
      content,
    ]
  );

  return {popup};
};
